import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { formatCurrency } from "./billingOverview.utils";
import FinancialQueueActionButtons from "./FinancialQueueActionButtons";
import { buildFollowUpRowActions } from "./financialQueueActions.utils";

const FinancialAttentionPanel = ({
  sections = [],
  loading = false,
  permissions = {},
  onAction = null,
}) => {
  const queueRows = useMemo(
    () =>
      (sections || [])
        .flatMap((section) =>
          (section.rows || []).map((row) => ({
            ...row,
            sectionId: section.id,
            sectionTitle: section.title,
            borderClass: section.borderClass,
          }))
        )
        .slice(0, 20),
    [sections]
  );

  if (loading) {
    return null;
  }

  if (queueRows.length === 0) {
    return null;
  }

  const renderActions = (row) => {
    if (!onAction) {
      return null;
    }

    return (
      <FinancialQueueActionButtons
        actions={buildFollowUpRowActions(row, permissions)}
        onAction={onAction}
        row={row}
        layout="stack"
      />
    );
  };

  return (
    <section
      id="needs-attention"
      className="scroll-mt-24 rounded-xl border border-amber-200 bg-amber-50/40 p-4 shadow-sm"
    >
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Needs follow-up</h2>
          <p className="mt-0.5 text-xs text-gray-600">
            Billing exceptions with amounts and quick enforcement actions.
          </p>
        </div>
        <span className="inline-flex w-fit rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
          {queueRows.length} item(s)
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-amber-100 bg-white">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Issue</th>
              <th className="px-4 py-2.5">Tenant</th>
              <th className="px-4 py-2.5">Outstanding</th>
              <th className="px-4 py-2.5">Details</th>
              {onAction ? <th className="min-w-[10.5rem] px-4 py-2.5">Actions</th> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {queueRows.map((row) => (
              <tr key={row.id} id={`attention-${row.sectionId}`} className="hover:bg-gray-50/80">
                <td className="px-4 py-3">
                  <span
                    className={`inline-block border-l-4 pl-2 text-xs font-medium text-gray-700 ${row.borderClass || "border-gray-300"}`}
                  >
                    {row.sectionTitle}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/tenants/${row.tenantId}?tab=billing`}
                    className="font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    {row.name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-xs text-gray-700">
                  {(row.outstanding_cents ?? 0) > 0 || (row.overdue_cents ?? 0) > 0 ? (
                    <div className="space-y-0.5">
                      {(row.outstanding_cents ?? 0) > 0 ? (
                        <div>{formatCurrency(row.outstanding_cents, row.currency || "AUD")}</div>
                      ) : null}
                      {(row.overdue_cents ?? 0) > 0 ? (
                        <div className="text-red-700">
                          {formatCurrency(row.overdue_cents, row.currency || "AUD")} overdue
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-4 py-3 text-xs capitalize text-gray-500">{row.detail}</td>
                {onAction ? (
                  <td className="px-4 py-3 align-top">{renderActions(row)}</td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default FinancialAttentionPanel;
